import React, { useContext, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { isLoggedIn, removeAllCookies, setAuthCookies } from "../../utils/auth";
import routes from "../../routes/routes";
import axios from "axios";
import { AUTH_URL } from "../../utils/url";
import { initiatePkceLogin, getStoredCodeVerifier } from "../../utils/pkce";
import { Button } from "../ui/button";
import { Spinner } from "../ui/spinner";
import { toast } from "sonner";
import { Role } from "../../enum/role";
import { getConfig } from "../../utils/permissions";
import { AuthContext } from "../../context/auth";

export default function Login() {
  const navigate = useNavigate();
  const { setLoginData } = useContext(AuthContext);
  const [loginResolved, setLoginResolved] = useState(false);

  const redirectToDefaultPage = (user = { role: Role.ARTIST }) => {
    if (getConfig("privilege.can-view-all-releases")) {
      return navigate(routes.allReleases);
    }
    navigate(routes.myReleases);
  };

  useEffect(() => {
    const urlString = window.location.href;
    const url = new URL(urlString);
    const code = url.searchParams.get("code");
    const error = url.searchParams.get("error");
    const errorDesc = url.searchParams.get("error_description");

    if (error) {
      toast.error("Authentication Error: " + error, {
        description: errorDesc,
      });
      setLoginResolved(true);
      return;
    }

    if (isLoggedIn()) {
      return redirectToDefaultPage();
    } else if (code) {
      const oauthApi = AUTH_URL + "/oauth/token";
      const params = new URLSearchParams();
      params.append("grant_type", "authorization_code");
      params.append("client_id", "application_client");
      params.append("redirect_uri", window.location.origin);
      params.append("code", code);

      // Append PKCE code_verifier if available
      const codeVerifier = getStoredCodeVerifier(true);
      if (codeVerifier) {
        params.append("code_verifier", codeVerifier);
      }

      const config = {
        headers: {
          "Content-Type": "application/x-www-form-urlencoded",
        },
      };

      axios
        .post(oauthApi, params, config)
        .then((tokenResponse) => {
          const tokenData = tokenResponse.data;
          setAuthCookies(tokenData);
          return axios
            .get(AUTH_URL + "/user/me", {
              headers: {
                Authorization: `Bearer ${tokenData.access_token}`,
              },
            })
            .then((userResponse) => {
              const user = userResponse.data.data.user;
              setAuthCookies(tokenData, user);
              setLoginData({ user, tokenResponse: tokenData });
              redirectToDefaultPage(user);
            });
        })
        .catch((err) => {
          console.error("OAuth token exchange error:", err);
          toast.error("Authentication failed", {
            description: err.response?.data?.message || err.message,
          });
          setLoginResolved(true);
        });
    } else {
      initiatePkceLogin();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <div className="w-full h-full bg-gradient-to-br flex items-center justify-center p-4">
      <div className="bg-white rounded-lg p-8 w-full max-w-md text-center border shadow-sm">
        <img
          src="https://www.rayapprelease.com/assets/images/logo-main.png"
          alt="RayApp Release"
          className="mx-auto mb-8 h-16 w-auto"
        />

        {loginResolved ? (
          <div className="space-y-4">
            <Button
              onClick={() => initiatePkceLogin()}
              className="w-full h-12 text-sm mb-2 rounded-lg"
            >
              Login
            </Button>
          </div>
        ) : (
          <div className="flex flex-col items-center space-y-4">
            <Spinner className="h-8 w-8 text-primary" />
            <div className="text-gray-600 text-sm">Authenticating with PKCE, please wait...</div>
          </div>
        )}
      </div>
    </div>
  );
}
